/**
 * Display helpers. Amounts arrive from the API as decimal strings and are formatted as strings —
 * never converted to floating-point numbers (spec §6.1).
 */
/** "0x12ab34cd…9f3c" style: keeps the start and end, which is what people compare. */
export function truncateMiddle(value, start = 6, end = 4) {
    if (value.length <= start + end + 1)
        return value;
    return `${value.slice(0, start)}…${value.slice(-end)}`;
}
/**
 * Adds thousands separators and trims the fraction to `maxDecimals` (rounding DOWN, so we never
 * show more than the user has). "1234567.123456789" → "1,234,567.123456".
 */
export function formatAmount(value, maxDecimals = 6) {
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
    if (!match)
        return value;
    const [, sign, whole, fraction = ""] = match;
    const grouped = whole.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const trimmed = fraction.slice(0, maxDecimals).replace(/0+$/, "");
    return `${sign}${grouped}${trimmed ? `.${trimmed}` : ""}`;
}
/** True for "0", "0.000" and similar. */
export function isZeroAmount(value) {
    return /^0*(\.0*)?$/.test(value.trim());
}
/**
 * Checks a user-typed amount: digits with at most `decimals` decimal places. Returns the
 * normalised string ("01.50" → "1.50") or null.
 */
export function normaliseAmountInput(input, decimals) {
    const value = input.trim().replace(/,/g, "");
    const match = /^(\d*)(?:\.(\d*))?$/.exec(value);
    if (!match || value === "" || value === ".")
        return null;
    const whole = (match[1] || "0").replace(/^0+(?=\d)/, "");
    const fraction = match[2] ?? "";
    if (fraction.length > decimals)
        return null;
    return fraction ? `${whole}.${fraction}` : whole;
}
/** Adds two decimal strings exactly (used for "amount + fee"). */
export function addDecimalStrings(a, b) {
    const [aw = "0", af = ""] = a.split(".");
    const [bw = "0", bf = ""] = b.split(".");
    const scale = Math.max(af.length, bf.length);
    const total = BigInt(aw + af.padEnd(scale, "0")) + BigInt(bw + bf.padEnd(scale, "0"));
    if (scale === 0)
        return total.toString();
    const digits = total.toString().padStart(scale + 1, "0");
    const fraction = digits.slice(-scale).replace(/0+$/, "");
    return fraction ? `${digits.slice(0, -scale)}.${fraction}` : digits.slice(0, -scale);
}
/** Compares two non-negative decimal strings: -1, 0 or 1. */
export function compareDecimalStrings(a, b) {
    const [aw = "0", af = ""] = a.split(".");
    const [bw = "0", bf = ""] = b.split(".");
    const scale = Math.max(af.length, bf.length);
    const x = BigInt(aw + af.padEnd(scale, "0"));
    const y = BigInt(bw + bf.padEnd(scale, "0"));
    return x < y ? -1 : x > y ? 1 : 0;
}
/** Subtracts b from a (a ≥ b expected); returns "0" instead of a negative result. */
export function subtractDecimalStrings(a, b) {
    if (compareDecimalStrings(a, b) <= 0)
        return "0";
    const [aw = "0", af = ""] = a.split(".");
    const [bw = "0", bf = ""] = b.split(".");
    const scale = Math.max(af.length, bf.length);
    const diff = BigInt(aw + af.padEnd(scale, "0")) - BigInt(bw + bf.padEnd(scale, "0"));
    if (scale === 0)
        return diff.toString();
    const digits = diff.toString().padStart(scale + 1, "0");
    const fraction = digits.slice(-scale).replace(/0+$/, "");
    return fraction ? `${digits.slice(0, -scale)}.${fraction}` : digits.slice(0, -scale);
}
/** "15 Oct 2026, 14:03" in the viewer's locale. */
export function formatDateTime(iso, locale) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime()))
        return iso;
    return d.toLocaleString(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}
/** Fills an explorer template ("…/tx/{hash}"). */
export function explorerLink(template, value, key = "hash") {
    return template.replace(`{${key}}`, encodeURIComponent(value));
}
const FIAT_SYMBOL = { USD: "$", SGD: "S$", GBP: "£" };
/**
 * "1234.56" + "SGD" → "S$1,234.56". Works on the decimal string (no floats); always 2 decimals.
 */
export function formatFiat(value, currency) {
    const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value.trim());
    if (!match)
        return value;
    const [, sign, whole, fraction = ""] = match;
    const grouped = whole.replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${sign ? "−" : ""}${FIAT_SYMBOL[currency] ?? `${currency} `}${grouped}.${fraction.padEnd(2, "0").slice(0, 2)}`;
}
/** Basis points → "0.5%", "-1.25%", "3%". */
export function formatBps(bps) {
    const sign = bps < 0 ? "-" : "";
    const abs = Math.abs(Math.trunc(bps));
    const whole = Math.floor(abs / 100);
    const frac = String(abs % 100)
        .padStart(2, "0")
        .replace(/0+$/, "");
    return `${sign}${whole}${frac ? `.${frac}` : ""}%`;
}
//# sourceMappingURL=format.js.map